import { Alert, LoadingButton, Paper, Typography } from "@firecms/ui";
import { type AdminContentDurationResult } from "@repo/common";
import { useQuery } from "@tanstack/react-query";

import AdminToolPage from "components/molecules/AdminToolPage";
import { formatDurationLabel } from "lib/format-duration.utils";
import { getErrorMessage } from "lib/error.utils";
import { queryKeys } from "lib/query-keys";
import { fetchAdminContentDuration } from "services/admin-tools/adminTools";

const TOOL_DESCRIPTION =
  "Summiert die Dauer aller veröffentlichten Tagesmeditationen und aller veröffentlichten Audio-Kurs-Lektionen (nur bereits freigeschaltete Lektionen mit `publish_date_milis` in der Vergangenheit).";

const AdminContentDuration: React.FC = () => {
  const { data, error, isFetching, refetch, isFetched } = useQuery<AdminContentDurationResult>({
    queryKey: queryKeys.adminTools.contentDuration(),
    queryFn: fetchAdminContentDuration,
    enabled: false,
  });

  const totalSeconds = (data?.meditationDurationInSeconds ?? 0) + (data?.audioCoursesDurationInSeconds ?? 0);

  return (
    <AdminToolPage title="Content-Dauer" description={TOOL_DESCRIPTION}>
      <LoadingButton loading={isFetching} disabled={isFetching} onClick={() => void refetch()}>
        Auswerten
      </LoadingButton>

      {error ? <Alert color="error">{getErrorMessage(error)}</Alert> : null}

      {isFetched && data ? (
        <Paper className="flex flex-col gap-4 p-4">
          <Typography variant="body2" className="text-gray-600 dark:text-gray-300">
            {data.publishedMeditationCount} veröffentlichte Tagesmeditationen und {data.publishedAudioCourseLessonCount}{" "}
            veröffentlichte Audio-Kurs-Lektionen ausgewertet.
          </Typography>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Typography variant="caption" color="secondary">
                Tagesmeditationen
              </Typography>
              <Typography>{formatDurationLabel(data.meditationDurationInSeconds, "hours")}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="secondary">
                Audio-Kurse
              </Typography>
              <Typography>{formatDurationLabel(data.audioCoursesDurationInSeconds, "hours")}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="secondary">
                Gesamt
              </Typography>
              <Typography className="font-medium">{formatDurationLabel(totalSeconds, "hours")}</Typography>
            </div>
          </div>

          <Typography variant="caption" color="secondary">
            {formatDurationLabel(totalSeconds, "minutes")} · {formatDurationLabel(totalSeconds, "seconds")}
          </Typography>
        </Paper>
      ) : null}
    </AdminToolPage>
  );
};

export default AdminContentDuration;
